import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from './SearchableSelect.module.css';

/**
 * Fuzzy-searchable single-select dropdown.
 *
 * Drop-in replacement for a native <select> that works with the `useForm`
 * hook: it emits a synthetic { target: { name, value } } event through
 * `onChange`, exactly like a native control, so callers need no special
 * wiring. Typing filters options with a lightweight fuzzy matcher
 * (subsequence match + contiguity/word-boundary scoring).
 */
const fuzzyScore = (query, text) => {
  if (!query) return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();

  // Fast paths that also produce the strongest scores.
  if (t === q) return 1000;
  if (t.startsWith(q)) return 800;
  const idx = t.indexOf(q);
  if (idx !== -1) return 600 - idx; // earlier contiguous match ranks higher

  // Subsequence match: every query char appears in order.
  let score = 0;
  let ti = 0;
  let prevMatch = -2;
  for (let qi = 0; qi < q.length; qi++) {
    const ch = q[qi];
    let found = -1;
    while (ti < t.length) {
      if (t[ti] === ch) {
        found = ti;
        break;
      }
      ti++;
    }
    if (found === -1) return -1; // not a subsequence -> no match
    score += 1;
    if (found === prevMatch + 1) score += 5; // reward contiguous runs
    if (found === 0 || t[found - 1] === ' ') score += 3; // reward word starts
    prevMatch = found;
    ti = found + 1;
  }
  return score;
};

const SearchableSelect = ({
  name,
  value,
  onChange,
  options = [],
  getOptionValue = (o) => o.id,
  getOptionLabel = (o) => `${o.first_name} ${o.last_name}`,
  placeholder = 'Select...',
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((o) => String(getOptionValue(o)) === String(value)),
    [options, value, getOptionValue]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    return options
      .map((o) => ({ o, score: fuzzyScore(query.trim(), getOptionLabel(o)) }))
      .filter((r) => r.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.o);
  }, [options, query, getOptionLabel]);

  // Close on outside click.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus the search box when the dropdown opens.
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const emitChange = (newValue) => {
    onChange({ target: { name, value: newValue, type: 'select' } });
  };

  const handleSelect = (option) => {
    emitChange(getOptionValue(option));
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={styles.container} ref={containerRef}>
      {/* Mirror the value in a real form control so native `required`
          validation still fires on submit. */}
      <input
        tabIndex={-1}
        className={styles.hiddenInput}
        value={value || ''}
        onChange={() => {}}
        required={required}
        aria-hidden="true"
      />

      <button
        type="button"
        className={`${styles.control} ${open ? styles.controlOpen : ''}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={selectedOption ? styles.value : styles.placeholder}>
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <span className={styles.arrow}>▾</span>
      </button>

      {open && (
        <div className={styles.dropdown}>
          <input
            ref={inputRef}
            type="text"
            className={styles.search}
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <ul className={styles.list}>
            {filtered.length === 0 && (
              <li className={styles.empty}>No matches</li>
            )}
            {filtered.map((option) => {
              const optValue = getOptionValue(option);
              const isSelected = String(optValue) === String(value);
              return (
                <li
                  key={optValue}
                  className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  {getOptionLabel(option)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
